import { useQuery } from '@tanstack/react-query';
import { queryKeys, unwrap, type components } from '@geefamily/api';
import { api } from '@/app/api';

export type Race = components['schemas']['RaceResponse'];
export type Standing = components['schemas']['StandingResponse'];
export type Leaderboard = components['schemas']['LeaderboardResponse'];
export type RacePhase = Race['phase'];
export type RaceMemberSummary = components['schemas']['RaceMemberSummaryResponse'];

const keys = queryKeys.module('races').entity('races');

/**
 * Short, because a race is a live thing: the countdown runs out, an import
 * lands, a race starts. The server caches for a minute and computes the phase
 * after that cache, so nothing here can show a finished race as running.
 */
const StaleTime = 30_000;

export function useRaces(take?: number) {
  return useQuery({
    queryKey: keys.list({ take: take ?? null }),
    queryFn: () =>
      unwrap<Race[]>(
        api.GET('/api/v1/races', take === undefined ? {} : { params: { query: { take } } }),
      ),
    staleTime: StaleTime,
  });
}

export function useRace(slug: string | undefined) {
  return useQuery({
    queryKey: keys.detail(slug ?? ''),
    queryFn: () =>
      unwrap<Race>(api.GET('/api/v1/races/{slug}', { params: { path: { slug: slug! } } })),
    enabled: Boolean(slug),
    staleTime: StaleTime,
  });
}

export function useLeaderboard(slug: string | undefined, page: number, pageSize = 50) {
  return useQuery({
    queryKey: [...keys.detail(slug ?? ''), 'leaderboard', { page, pageSize }] as const,
    queryFn: () =>
      unwrap<Leaderboard>(
        api.GET('/api/v1/races/{slug}/leaderboard', {
          params: { path: { slug: slug! }, query: { page, pageSize } },
        }),
      ),
    enabled: Boolean(slug),
    staleTime: StaleTime,
    // Paging a leaderboard should not blank the table while the next page
    // loads — the rows are the whole point of the screen.
    placeholderData: (previous) => previous,
  });
}

/**
 * The signed-in member's record across every race they appear in.
 *
 * Public surface of this feature, consumed by the profile page. A profile that
 * reached into `features/races/components` would be reaching into internals; a
 * hook exported from the feature's api module is what that module is for.
 */
export function useMyRaceSummary(enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.module('races').entity('me').all, 'summary'] as const,
    queryFn: () => unwrap<RaceMemberSummary>(api.GET('/api/v1/races/me/summary', {})),
    enabled,
    staleTime: StaleTime,
  });
}
